import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { TableController } from '../../../components/Constant'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { FINANCIAL_REPORTS, GET_VENDOR_LIST_FILTER } from '../../../repository/revenue_reports_repo'
import toast from 'react-hot-toast'
import moment from 'moment-timezone'

export const useFinancialReport = () => {
    const { t } = useTranslation()
    const [controller, setController] = useState(TableController)
    const [loader, setLoader] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const location = useLocation()
    const [details, setDetails] = useState([])
    const [otherInformation, setOtherInformation] = useState([])
    const [filter, setFilter] = useState({})
    const [vendorListData, setVendorListData] = useState([])

    const handlePagination = (obj) => setController(obj)

    const reportKey = useMemo(() => {
        const pathSegments = location.pathname.split('/').filter(Boolean)
        const lastSegment = pathSegments[pathSegments.length - 1]
        return lastSegment
    }, [location.pathname])

    const [filterInputs, setFilterInputs] = useState({
        startDate: null,
        endDate: null,
        filter: { name: 'Year', value: 'YEAR' },
        vendor_id: null,
        listType: { name: 'All', value: 'ALL' },
    })
    const filterRef = useRef(filterInputs)
    const getUserReport = useCallback(
        async (isDownload) => {
            setLoader(true)
            let payload = {
                type: reportKey,
                startDate: moment(filterRef?.current?.startDate).startOf('day'),
                endDate: moment(filterRef?.current?.endDate).endOf('day'),
                filter: filterRef?.current?.filter?.value,
                vendor_id: filterRef?.current?.vendor_id?._id,
                isDownloadExcel: isDownload,
                listType: filterRef?.current?.listType?.value,
                page: controller.page,
                count: controller.rowsPerPage,
            }
            try {
                let response = await FINANCIAL_REPORTS(payload)
                if (isDownload === false) {
                    setDetails(response?.data?.result)
                    setOtherInformation(response.otherInfo)
                    setTotalCount(response.totalCount)
                } else {
                    window.open(response?.data)
                }
                setLoader(false)
            } catch (error) {
                setDetails([])
                setLoader(false)
                toast.error(error.message)
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [controller, reportKey]
    )

    useEffect(() => {
        filterRef.current = filterInputs
    }, [filterInputs])

    const getVendorList = useCallback(async () => {
        setLoader(true)
        const payload = { count: 1000 }
        try {
            let response = await GET_VENDOR_LIST_FILTER(payload)
            setVendorListData(response?.data?.map((cur) => ({ ...cur, fullName: `${cur?.firstName}  ${cur?.lastName}` })))
            setLoader(false)
        } catch (error) {
            toast.error(error.message)
            setVendorListData([])
            setLoader(false)
        }
    }, [])

    useEffect(() => {
        getUserReport(false)
        getVendorList()
    }, [getUserReport, getVendorList])

    const bookingTableHead = [
        { label: '#' },
        { label: `${t('reportFilter.bookingFilter.bookingType')} / ${t('tableTitle.bookingReferenceNumber')}` },
        { label: `${t('boxTitle.booking')} ${t('tableTitle.date')}` },
        { label: `${t('tableTitle.vendorName')} / ${t('tableTitle.businessName')}` },
        { label: `${t('boxTitle.user')}` },
        { label: `${t('boxTitle.totalAmount')}` },
        { label: `${t('tableTitle.extraCharges')}` },
        { label: `${t('tableTitle.commissionCharges')}` },
        { label: `${t('tableTitle.amountToReceive')}` },
        { label: `${t('tableTitle.settled')}` },
        { label: `${t('tableTitle.unsettled')}` },
        { label: `${t('boxTitle.refund')}` },
    ]

    const paymentCommissionTableHead = [
        { label: '#' },
        { label: `${t('reportFilter.bookingFilter.bookingType')} / ${t('tableTitle.bookingReferenceNumber')}` },
        { label: `${t('boxTitle.booking')} ${t('tableTitle.date')}` },
        { label: `${t('boxTitle.user')}` },
        { label: `${t('boxTitle.totalAmount')}` },
        { label: `${t('tableTitle.extraCharges')}` },
        { label: `${t('tableTitle.commissionCharges')}` },
        { label: `${t('tableTitle.amountToReceive')}` },
        { label: `${t('tableTitle.settled')}` },
        { label: `${t('tableTitle.unsettled')}` },
        { label: `${t('boxTitle.refund')}` },
    ]

    const paymentRecievedTableHead = [{ label: '#' }, { label: `${t('tableTitle.name')}` }, { label: `${t('tableTitle.date')}` }, { label: `${t('boxTitle.totalAmount')}` }]
    const revenueBreakdownTableHead = [{ label: '#' }, { label: `${t('tableTitle.name')}` }, { label: `${t('tableTitle.description')}` }, { label: `${t('boxTitle.totalAmount')}` }]
    const paymentReleaseTableHead = [
        { label: '#' },
        { label: `${t('tableTitle.vendorName')}` },
        { label: `${t('tableTitle.amount')}` },
        { label: `${t('tableTitle.bookingReferenceNumber')}` },
        { label: `${t('tableTitle.date')}` },
    ]

    const handleFilterInputChange = (name, value) => {
        setFilterInputs((prev) => ({
            ...prev,
            [name]: name !== 'filter' && name !== 'vendor_id' && name !== 'listType' ? value : value[0],
        }))
    }

    const reset = () => {
        const cleared = { startDate: null, endDate: null, filter: { name: 'Year', value: 'YEAR' }, listType: { name: 'All', value: 'ALL' } }
        setFilterInputs(cleared)
        filterRef.current = cleared
        getUserReport(false)
    }

    const search = () => getUserReport(false)

    return {
        data: {
            controller,
            loader,
            totalCount,
            bookingTableHead,
            details,
            filter,
            otherInformation,
            filterInputs,
            paymentCommissionTableHead,
            paymentRecievedTableHead,
            vendorListData,
            revenueBreakdownTableHead,
            paymentReleaseTableHead,
        },
        methods: { handlePagination, setLoader, setTotalCount, setFilter, handleFilterInputChange, reset, search, getUserReport },
    }
}
